import { ReactNode, useMemo, useState } from 'react';

import { Add, Check, Close, OpenInNew } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  SxProps,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Path, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import strings from 'constants/index';
import { GenerateFieldType } from 'hooks/forms/useFieldGenerator';
import { AnyControl } from 'types/rhf';
import {
  buildControlCenterLink,
  ControlCenterEntityType,
  isVesselIdKey,
} from 'utils/controlCenter.utils';
import {
  ExternalIds,
  getLocalOnlyExternalIdKeys,
  getVisibleExternalIdKeys,
  mapExternalIdsToProperties,
  normalizeExternalIdsForForm,
} from 'utils/externalIds.utils';

import PropertiesCard from '../PropertiesCard/PropertiesCard';
import {
  buildExternalIdsEditableProperties,
  EXTERNAL_IDS_FIELD,
} from './buildExternalIdsEditableProperties';
import { getLocalExternalIdEntrySx } from './ExternalIdsCard.styles';

interface ExternalIdsCardProps {
  externalIds?: ExternalIds;
  control?: AnyControl;
  generateField?: GenerateFieldType<any>;
  setValue?: UseFormSetValue<any>;
  getValues?: UseFormGetValues<any>;
  editingKey?: Path<any> | null;
  title?: string;
  sx?: SxProps;
  controlCenterEntityType?: ControlCenterEntityType;
}

const ExternalIdsCard = ({
  externalIds,
  control,
  generateField,
  setValue,
  getValues,
  editingKey = null,
  title = strings.externalIDs,
  sx,
  controlCenterEntityType,
}: ExternalIdsCardProps) => {
  const theme = useTheme();
  const localEntrySx = useMemo(() => getLocalExternalIdEntrySx(theme), [theme]);
  const isEditable = Boolean(control && generateField && setValue && getValues);
  const [isAdding, setIsAdding] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [addError, setAddError] = useState('');

  const watchedExternalIds = useWatch({
    control,
    name: EXTERNAL_IDS_FIELD,
    disabled: !isEditable,
  }) as Record<string, string> | undefined;

  const formExternalIds = isEditable
    ? (watchedExternalIds ?? getValues?.(EXTERNAL_IDS_FIELD) ?? {})
    : {};

  const localOnlyKeys = useMemo(
    () =>
      isEditable ? getLocalOnlyExternalIdKeys(formExternalIds, externalIds) : new Set<string>(),
    [externalIds, formExternalIds, isEditable],
  );

  const properties = useMemo(() => {
    if (!isEditable) {
      return mapExternalIdsToProperties(externalIds);
    }

    return buildExternalIdsEditableProperties({
      keys: getVisibleExternalIdKeys(formExternalIds),
      control,
      generateField: generateField!,
      setValue: setValue!,
      editingKey,
      isAdding,
      localOnlyKeys,
      localEntrySx,
    });
  }, [
    control,
    editingKey,
    externalIds,
    formExternalIds,
    generateField,
    isAdding,
    isEditable,
    localEntrySx,
    localOnlyKeys,
    setValue,
  ]);

  const linkableExternalIds = useMemo(
    () => (isEditable ? formExternalIds : normalizeExternalIdsForForm(externalIds)),
    [externalIds, formExternalIds, isEditable],
  );

  const decoratedProperties = useMemo(() => {
    if (!controlCenterEntityType) {
      return properties;
    }

    return properties.map(property => {
      const propertyName = (property as { name?: string }).name;
      if (!propertyName || !isVesselIdKey(propertyName)) {
        return property;
      }
      if (localOnlyKeys.has(propertyName)) {
        return property;
      }

      const link = buildControlCenterLink(
        controlCenterEntityType,
        linkableExternalIds[propertyName],
      );
      if (!link) {
        return property;
      }

      return {
        ...property,
        value: (
          <MuiLink
            href={link}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {(property as { value: ReactNode }).value}
            <OpenInNew fontSize="inherit" />
          </MuiLink>
        ),
      };
    });
  }, [controlCenterEntityType, linkableExternalIds, localOnlyKeys, properties]);

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewKey('');
    setNewValue('');
    setAddError('');
  };

  const handleConfirmAdd = () => {
    const trimmedKey = newKey.trim();
    const trimmedValue = newValue.trim();

    if (!trimmedKey) {
      setAddError(`${strings.source} is required`);
      return;
    }

    if (!trimmedValue) {
      setAddError(`${strings.sourceEntityId} is required`);
      return;
    }

    const current = getValues!(EXTERNAL_IDS_FIELD) || {};
    if (current[trimmedKey] !== undefined && current[trimmedKey] !== '') {
      setAddError('An external ID with this source already exists');
      return;
    }

    setValue!(
      EXTERNAL_IDS_FIELD,
      { ...current, [trimmedKey]: trimmedValue },
      { shouldDirty: true, shouldValidate: true },
    );
    handleCancelAdd();
  };

  const showCard = decoratedProperties.length > 0 || isEditable;

  if (!showCard) {
    return <></>;
  }

  return (
    <PropertiesCard sx={sx} title={title} properties={decoratedProperties} listTestId="external-ids-list">
      {isEditable && (
        <Box px={2} pb={2} pt={decoratedProperties.length ? 0 : 1}>
          {isAdding ? (
            <Box display="flex" flexDirection="column" gap={1}>
              <TextField
                label={strings.source}
                value={newKey}
                onChange={e => {
                  setNewKey(e.target.value);
                  setAddError('');
                }}
                size="small"
                fullWidth
                autoFocus
              />
              <TextField
                label={strings.sourceEntityId}
                value={newValue}
                onChange={e => {
                  setNewValue(e.target.value);
                  setAddError('');
                }}
                size="small"
                fullWidth
              />
              {addError && (
                <Typography variant="caption" color="error">
                  {addError}
                </Typography>
              )}
              <Box display="flex" gap={1} alignItems="center">
                <IconButton
                  aria-label="confirm-add-external-id"
                  onClick={handleConfirmAdd}
                  size="large"
                  color="primary"
                >
                  <Check />
                </IconButton>
                <IconButton
                  aria-label="cancel-add-external-id"
                  onClick={handleCancelAdd}
                  size="large"
                >
                  <Close />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Button
              startIcon={<Add />}
              onClick={() => setIsAdding(true)}
              disabled={Boolean(editingKey)}
              size="small"
            >
              {strings.add} {strings.externalIDs}
            </Button>
          )}
        </Box>
      )}
    </PropertiesCard>
  );
};

export default ExternalIdsCard;
